import { safeGet } from '../../../core/utils/safe-get.js';
import { singleLineName } from '../../../core/utils/single-line-name.js';
import type {
  DatapointMetadata,
  DotSeriesWithDatapoints,
  XYChartAxisInternal,
  XYChartDotSeriesInternal,
  XYChartVariantsInternal,
} from '../../types/xy-chart-internals.js';
import type { XYChartSelectionLinearMetadata } from '../../types/xy-chart-selection.js';
import {
  type DotDatapoint,
  type XYChartColorPalette,
  type XYChartData,
} from '../../types/xy-chart.js';
import { isDotSeries } from '../../utils/type-guards.js';
import type { MutableProcessingContext } from '../types/processing-types.js';
import {
  getDatumValue,
  getDotDatapoint,
} from '../utils/access-datapoint/get-datapoints.js';
import { getCalculatedLinearPoint } from '../utils/cast-data/get-calculated-linear-point.js';
import { getLinearColor } from '../utils/coloring/get-linear-color-palette.js';
import { SeriesBaseStrategy } from './base-series.js';

export class DotSeriesStrategy extends SeriesBaseStrategy<XYChartSelectionLinearMetadata> {
  canHandle(
    seriesProps: XYChartVariantsInternal,
  ): seriesProps is XYChartDotSeriesInternal {
    return isDotSeries(seriesProps);
  }

  // can this method be un base?
  protected getSeriesId(
    datum: XYChartData,
    seriesProps: XYChartDotSeriesInternal,
  ): string {
    const seriesIdAccessor = getDatumValue(
      datum,
      seriesProps.seriesIdAccessor,
    )?.toString() as string;
    return seriesIdAccessor ?? seriesProps.id;
  }

  protected createSeriesMetadata(
    datum: XYChartData,
    seriesProps: XYChartDotSeriesInternal,
    xAxis: XYChartAxisInternal,
    yAxis: XYChartAxisInternal,
    seriesId: string,
    context: MutableProcessingContext,
  ) {
    const { intl, seriesIndexes } = context;
    const { nameAccessor: seriesNameAccessor } = seriesProps;

    const seriesIndex = this.getOrCreateSeriesIndex(seriesId, seriesIndexes);
    const seriesName = this.getSeriesName(
      intl,
      seriesIndex,
      // APPDEV-15290 - Adjust after removing name prop from series
      // oxlint-disable-next-line typescript/no-deprecated
      seriesProps.name,
      {
        seriesNameAccessor,
        datum,
      },
    );

    const name = singleLineName(seriesName);
    const color = this.getColor(
      datum,
      seriesProps,
      context.colorPalette,
      name,
      seriesIndex,
    );

    return {
      xAxis,
      yAxis,
      actions: seriesProps.actions,
      ...color,
      name,
      nameSegments: Array.isArray(seriesName) ? seriesName : undefined,
      shape: seriesProps.shape,
    };
  }

  protected getCalculationPoint(
    datapoint: DotDatapoint,
    xAxis: XYChartAxisInternal,
    yAxis: XYChartAxisInternal,
  ) {
    return getCalculatedLinearPoint(datapoint, xAxis, yAxis);
  }

  protected processImpl(
    datum: XYChartData,
    seriesProps: XYChartDotSeriesInternal,
    xAxis: XYChartAxisInternal,
    yAxis: XYChartAxisInternal,
    datapoint: DotDatapoint,
    context: MutableProcessingContext,
  ) {
    const { seriesMap, seriesToMetadata } = context;

    const seriesId = this.getSeriesId(datum, seriesProps);
    const isSeriesVisible =
      context.filteredSeriesIds === undefined ||
      context.filteredSeriesIds.has(seriesId);

    let currentSeries: DotSeriesWithDatapoints | undefined =
      seriesMap.get(seriesId);

    if (!currentSeries) {
      currentSeries = this.createSeries(
        seriesId,
        seriesProps,
        xAxis,
        yAxis,
      ) as DotSeriesWithDatapoints;

      this.registerSeries(currentSeries, seriesId, context);

      const metadata = this.createSeriesMetadata(
        datum,
        seriesProps,
        xAxis,
        yAxis,
        seriesId,
        context,
      );

      seriesToMetadata.set(currentSeries, {
        ...metadata,
        isVisible: isSeriesVisible,
      });
    }

    const datapointMetadata: DatapointMetadata = { isGap: false };

    this.registerDatapoint(
      datapoint,
      datapointMetadata,
      currentSeries,
      context,
    );

    const calcPoint = this.getCalculationPoint(datapoint, xAxis, yAxis);

    this.updateAxisDomains(
      calcPoint,
      xAxis,
      yAxis,
      context.axisIdToScaleDomain,
      isSeriesVisible,
    );

    if (isSeriesVisible) {
      context.axisWithSeries.add(xAxis.id);
      context.axisWithSeries.add(yAxis.id);

      const currentSeriesMetadata = safeGet(seriesToMetadata, currentSeries);

      this.addValue({
        series: currentSeries,
        datapoint,
        color: currentSeriesMetadata?.color,
      });
    }
  }

  getColor(
    datum: XYChartData,
    seriesProps: XYChartDotSeriesInternal,
    configColorPalette: XYChartColorPalette | undefined,
    seriesName: string,
    seriesIndex: number,
  ) {
    return getLinearColor(
      datum,
      seriesProps,
      configColorPalette,
      seriesName,
      seriesIndex,
    );
  }

  getDatapoint(
    datum: XYChartData,
    seriesProps: XYChartDotSeriesInternal,
    xAxis: XYChartAxisInternal,
    yAxis: XYChartAxisInternal,
  ) {
    return getDotDatapoint(datum, seriesProps, xAxis, yAxis);
  }
}
